import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { taiKhoanRepository } from '../../../lib/repositories/tai-khoan.repository';
import { useToast } from '../../../lib/toast';
import { useKenhSuKienStore } from '../../../stores/kenh-su-kien';
import type { HanhDongKeycloak, PhienDangNhap, XacThucCuaToi } from '../types';

const KHOA_XAC_THUC = ['tai-khoan', 'xac-thuc'] as const;
const KHOA_PHIEN = ['tai-khoan', 'phien'] as const;
const KHOA_KET_QUA = ['tai-khoan', 'ket-qua-hanh-dong'] as const;

/**
 * Trạng thái OTP và mật khẩu. `staleTime: 0`: người dùng vừa cài hay gỡ OTP ở
 * trang Keycloak (có thể ở tab khác) thì quay lại là thấy trạng thái mới.
 */
export function useXacThucCuaToi() {
  return useQuery<XacThucCuaToi>({
    queryKey: KHOA_XAC_THUC,
    queryFn: taiKhoanRepository.xacThuc,
    staleTime: 0,
  });
}

/**
 * Chu kỳ tải lại danh sách phiên khi màn đang mở.
 *
 * Kênh sự kiện đang chạy (`trucTiep`) thì `session.changed` làm mới danh sách
 * ngay, nên tải định kỳ chỉ còn là lưới an toàn — cho sự kiện rơi mất lúc nối
 * lại, hay thay đổi phía Keycloak mà backend không phát sự kiện — và nới ra
 * 120 giây. Không có kênh (dữ liệu mẫu, cổng nền tảng nhận 403, đang mất kết
 * nối) thì giữ 30 giây như trước.
 */
const CHU_KY_TAI_LAI_PHIEN_MS = 30_000;
const CHU_KY_TAI_LAI_PHIEN_KHI_TRUC_TIEP_MS = 120_000;

export function usePhienDangNhap() {
  const trucTiep = useKenhSuKienStore((s) => s.trangThai === 'trucTiep');
  return useQuery<PhienDangNhap[]>({
    queryKey: KHOA_PHIEN,
    queryFn: taiKhoanRepository.phien,
    staleTime: 0,
    refetchInterval: trucTiep ? CHU_KY_TAI_LAI_PHIEN_KHI_TRUC_TIEP_MS : CHU_KY_TAI_LAI_PHIEN_MS,
    refetchOnWindowFocus: true,
  });
}

/** Kênh sự kiện báo phiên vừa đổi (`session.changed`): tải lại danh sách phiên nếu màn đang mở. */
export function lamMoiPhienDangNhap(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: KHOA_PHIEN });
}

/**
 * Kết quả action vừa quay về từ Keycloak (`kc_action_status`), đọc **một lần**.
 *
 * Đi qua query thay vì `useEffect`: kết quả là dữ liệu của lượt tải trang,
 * `gcTime: 0` bỏ nó khi rời màn nên quay lại không hiện lại. Có kết quả thì
 * làm mới trạng thái OTP và phiên — Keycloak vừa đổi cả hai.
 */
export function useKetQuaHanhDong() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: KHOA_KET_QUA,
    queryFn: () => {
      const kq = taiKhoanRepository.ketQuaHanhDong();
      if (kq) {
        void queryClient.invalidateQueries({ queryKey: KHOA_XAC_THUC });
        void queryClient.invalidateQueries({ queryKey: KHOA_PHIEN });
      }
      return kq;
    },
    staleTime: Infinity,
    gcTime: 0,
  });
  const boQua = () => queryClient.setQueryData(KHOA_KET_QUA, null);
  return { ketQua: query.data ?? null, boQua };
}

/**
 * Chuyển sang trang Keycloak cho một action, xong quay về đúng màn này.
 *
 * Chế độ dữ liệu mẫu không có Keycloak: báo bằng toast thay vì để nút im lặng.
 */
export function useMoHanhDong() {
  const { pathname } = useLocation();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();

  return (hanhDong: HanhDongKeycloak) => {
    if (!taiKhoanRepository.moDuocHanhDong()) {
      toast.info(t('taiKhoan.canKeycloak'));
      return;
    }
    taiKhoanRepository.moHanhDong(hanhDong, pathname).catch((err) => toast.error(dichLoi(err)));
  };
}

export function useDangXuatPhien() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();

  return useMutation({
    mutationFn: (bien: { id: string } | { tatCaPhienKhac: true }) =>
      'id' in bien ? taiKhoanRepository.dangXuatPhien(bien.id) : taiKhoanRepository.dangXuatPhienKhac(),
    onSuccess: (_d, bien) => {
      void queryClient.invalidateQueries({ queryKey: KHOA_PHIEN });
      toast.success(t('id' in bien ? 'taiKhoan.daDangXuatPhien' : 'taiKhoan.daDangXuatPhienKhac'));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}
